import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { AI_TOKEN_CALCULATOR_ENDPOINT } from "../../config/urls";
import { useAuth } from "../../hooks/AuthProvider";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";
import NumberSliderField from "../../common/number-slider-field";
import CostExplainer from "../../common/cost-explainer";
import { formatAICost, getCurrencySymbol } from "../../utils/currency";

const RECALC_DELAY_MS = 400;

const CALL_LABELS = {
  rewrite: "Rewrite the question",
  answer: "Write the answer",
  query_embedding: "Look up the question in the knowledge base",
};

const DEFAULT_INPUTS = {
  users: 100,
  questions_per_user: 20,
  question_words: 30,
  answer_words: 60,
  retrieved_documents: 5,
  chunk_words: 500,
  history_messages: 10,
  rewrite: true,
  history_answer_chars: 300,
  max_answer_tokens: 250,
  skip_first_rewrite: false,
  questions_per_conversation: 3,
};

const paperStyle = { p: 3, borderRadius: "8px", boxShadow: "0px 1px 5px 0px #0000001A" };

const SectionTitle = ({ children }) => (
  <Typography sx={{ ...typography.h2, fontWeight: 600 }}>{children}</Typography>
);

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const priceLabel = (model) =>
  model.input_per_mtok === null || model.input_per_mtok === undefined
    ? "no price yet"
    : `$${model.input_per_mtok} in / $${model.output_per_mtok} out per 1M tokens`;

const tokenStepLines = (explanation, basis) => {
  const parts = explanation?.token_parts || {};
  if (basis === "measured") {
    return [
      `Your measured average: ${formatNumber(parts.measured_input)} input and ${formatNumber(parts.measured_output)} output tokens per question, across the rewrite and answer calls.`,
      "Plus a lookup of each question in the knowledge base (an embedding call).",
    ];
  }
  const lines = [
    `Answer call input: instructions ${formatNumber(parts.instructions)} + retrieved documents ${formatNumber(parts.documents)} + chat history ${formatNumber(parts.history)} + the question ${formatNumber(parts.question)} tokens.`,
    `Answer call output: about ${formatNumber(parts.answer)} tokens.`,
  ];
  if (parts.rewrite_instructions !== undefined) {
    lines.push(
      `Rewrite call: instructions ${formatNumber(parts.rewrite_instructions)} + history + the question in, a rewritten question out.`
    );
  }
  lines.push("A lookup of the rewritten question in the knowledge base (an embedding call).");
  return lines;
};

const AITokenCalculator = () => {
  const { getValidToken } = useAuth();

  const [options, setOptions] = useState(null);
  const [model, setModel] = useState("");
  const [embeddingModel, setEmbeddingModel] = useState("");
  const [inputs, setInputs] = useState(DEFAULT_INPUTS);
  const [basis, setBasis] = useState("settings");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const requestId = useRef(0);

  const request = useCallback(
    async (method, body) => {
      const token = await getValidToken();
      const response = await fetch(AI_TOKEN_CALCULATOR_ENDPOINT, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": localStorage.getItem("tenant"),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || "Request failed");
      return data;
    },
    [getValidToken]
  );

  // Models, prices and the bot's own settings, once.
  useEffect(() => {
    request("GET")
      .then(({ data }) => {
        setOptions(data);
        setModel(data.default_model);
        setEmbeddingModel(data.embedding?.model || "");
        setInputs((current) => ({ ...current, ...data.defaults }));
        setBasis(data.measured?.available ? "measured" : "settings");
      })
      .catch((err) => setError(`Couldn't load the calculator: ${err.message}`));
  }, [request]);

  // Recalculate shortly after any change; only the latest response is shown.
  useEffect(() => {
    if (!options || !model) return undefined;
    const timer = setTimeout(async () => {
      const id = ++requestId.current;
      setLoading(true);
      try {
        const data = await request("POST", { model, embedding_model: embeddingModel, basis, ...inputs });
        if (id === requestId.current) {
          setResult(data);
          setError(null);
        }
      } catch (err) {
        if (id === requestId.current) {
          setResult(null);
          setError(err.message);
        }
      } finally {
        if (id === requestId.current) setLoading(false);
      }
    }, RECALC_DELAY_MS);
    return () => clearTimeout(timer);
  }, [options, model, embeddingModel, basis, inputs, request]);

  const setInput = (key) => (value) => setInputs((current) => ({ ...current, [key]: value }));

  if (!options) {
    return (
      <Box sx={{ p: 3 }}>
        {error ? <Alert severity="error">{error}</Alert> : <CircularProgress />}
      </Box>
    );
  }

  const measured = options.measured || {};
  const settingsDisabled = basis === "measured";
  // Costs come converted to the tenant's billing currency by the backend; model prices stay USD.
  const currency = result?.currency || "USD";
  const money = (value) => formatAICost(value, currency);
  const rateText =
    currency !== "USD" && result ? `1 USD = ${getCurrencySymbol(currency)}${result.conversion_rate}` : undefined;

  return (
    <Box sx={{ p: 3, bgcolor: "#FAFAFA" }}>
      <Grid container spacing={3}>
        {/* ---- Inputs ---- */}
        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            <Paper elevation={0} sx={paperStyle}>
              <Stack spacing={2}>
                <SectionTitle>Model</SectionTitle>
                <TextField
                  select
                  label="Chat model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  fullWidth
                  inputProps={{ "data-testid": "calculator-model" }}
                >
                  {options.models.map((m) => (
                    <MenuItem key={m.model} value={m.model} disabled={m.input_per_mtok === null}>
                      <Stack>
                        <span>{m.display_name}</span>
                        <Typography variant="caption" color="text.secondary">
                          {priceLabel(m)}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Embedding model (knowledge-base lookups and training)"
                  value={embeddingModel}
                  onChange={(e) => setEmbeddingModel(e.target.value)}
                  fullWidth
                  inputProps={{ "data-testid": "calculator-embedding-model" }}
                  helperText={
                    embeddingModel !== options.embedding?.model
                      ? "Your bot uses a different embedding model; switching means re-training the knowledge base."
                      : "The model your bot uses now."
                  }
                >
                  {options.embedding_models.map((m) => (
                    <MenuItem key={m.model} value={m.model} disabled={m.input_per_mtok === null}>
                      <Stack>
                        <span>{m.display_name}</span>
                        <Typography variant="caption" color="text.secondary">
                          {m.input_per_mtok === null ? "no price yet" : `$${m.input_per_mtok} per 1M tokens`}
                        </Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </TextField>
                {options.own_key_only_models?.length > 0 && (
                  <Typography sx={{ ...typography.bodySmall, color: "text.secondary" }} data-testid="calc-own-key-note">
                    Showing the models you can use now.{" "}
                    {options.own_key_only_models.map((m) => m.display_name).join(", ")} need your own{" "}
                    {[...new Set(options.own_key_only_models.map((m) => m.provider_name))].join(" / ")} key, connected
                    under Integrations.
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper elevation={0} sx={paperStyle}>
              <Stack spacing={2}>
                <SectionTitle>Usage</SectionTitle>
                <NumberSliderField
                  label="Customers per month"
                  value={inputs.users}
                  onChange={setInput("users")}
                  min={1}
                  max={1000000}
                  logScale
                  testId="calc-users"
                />
                <NumberSliderField
                  label="Questions per customer per month"
                  value={inputs.questions_per_user}
                  onChange={setInput("questions_per_user")}
                  min={1}
                  max={10000}
                  logScale
                  testId="calc-questions"
                />
              </Stack>
            </Paper>

            <Paper elevation={0} sx={paperStyle}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                  <SectionTitle>Tokens per question</SectionTitle>
                  <ToggleButtonGroup
                    value={basis}
                    exclusive
                    size="small"
                    onChange={(e, value) => value && setBasis(value)}
                    sx={{ "& .MuiToggleButton-root": { textTransform: "none" } }}
                  >
                    <ToggleButton value="measured" disabled={!measured.available}>
                      Your last {measured.days || 30} days
                      {measured.available ? ` (${formatNumber(measured.questions)} questions)` : ""}
                    </ToggleButton>
                    <ToggleButton value="settings">Estimate from settings</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                {!measured.available && (
                  <Typography sx={{ ...typography.bodySmall, color: "text.secondary" }}>
                    Your measured average needs at least {measured.min_questions || 20} questions in the last{" "}
                    {measured.days || 30} days (so far: {formatNumber(measured.questions)}). Estimating from your
                    bot's settings instead.
                  </Typography>
                )}
                {settingsDisabled && (
                  <Typography sx={{ ...typography.bodySmall, color: "text.secondary" }}>
                    Using what your bot actually used per question. Switch to "Estimate from settings" to try other
                    settings.
                  </Typography>
                )}

                <NumberSliderField label="Question size" unit="words" value={inputs.question_words}
                  onChange={setInput("question_words")} min={1} max={2000} disabled={settingsDisabled}
                  testId="calc-question-words" />
                <NumberSliderField label="Answer size" unit="words" value={inputs.answer_words}
                  onChange={setInput("answer_words")} min={1} max={10000} disabled={settingsDisabled}
                  testId="calc-answer-words" />
                <NumberSliderField label="Documents retrieved per question" value={inputs.retrieved_documents}
                  onChange={setInput("retrieved_documents")} min={1} max={20} disabled={settingsDisabled}
                  tooltip="Knowledge-base chunks added to each answer call" testId="calc-documents" />
                <NumberSliderField label="Chunk size" unit="words" value={inputs.chunk_words}
                  onChange={setInput("chunk_words")} min={50} max={2000} step={50} disabled={settingsDisabled}
                  testId="calc-chunk" />
                <NumberSliderField label="Chat history messages" value={inputs.history_messages}
                  onChange={setInput("history_messages")} min={0} max={50} disabled={settingsDisabled}
                  tooltip="Earlier messages sent along so the bot keeps context" testId="calc-history" />
                <NumberSliderField label="Characters kept from earlier answers" value={inputs.history_answer_chars}
                  onChange={setInput("history_answer_chars")} min={50} max={4000} step={50} disabled={settingsDisabled}
                  tooltip="Earlier answers are shortened to this when sent as history (your bot's setting)" testId="calc-history-chars" />
                <NumberSliderField label="Maximum answer length" unit="tokens" value={inputs.max_answer_tokens}
                  onChange={setInput("max_answer_tokens")} min={50} max={2000} step={10} disabled={settingsDisabled}
                  tooltip="Your bot's cap on each answer; answers longer than this are cut" testId="calc-max-tokens" />
                <NumberSliderField label="Questions per conversation" value={inputs.questions_per_conversation}
                  onChange={setInput("questions_per_conversation")} min={1} max={20} disabled={settingsDisabled}
                  tooltip="Average questions a customer asks in one chat; with the first-question rewrite skip on, only follow-ups pay for a rewrite"
                  testId="calc-questions-per-conversation" />
                <FormControlLabel
                  disabled={settingsDisabled}
                  control={
                    <Switch
                      checked={inputs.rewrite}
                      onChange={(e) => setInput("rewrite")(e.target.checked)}
                      sx={{ "& .Mui-checked": { color: ColorsConstants.primaryBlue100 } }}
                    />
                  }
                  label={<Typography sx={typography.bodyLarge}>Rewrite each question before searching (an extra small AI call)</Typography>}
                />
                <FormControlLabel
                  disabled={settingsDisabled || !inputs.rewrite}
                  control={
                    <Switch
                      checked={!!inputs.skip_first_rewrite}
                      onChange={(e) => setInput("skip_first_rewrite")(e.target.checked)}
                      sx={{ "& .Mui-checked": { color: ColorsConstants.primaryBlue100 } }}
                    />
                  }
                  label={<Typography sx={typography.bodyLarge}>Skip the rewrite on first questions (your bot's cost saver)</Typography>}
                />
                <Box>
                  <Button
                    size="small"
                    disabled={settingsDisabled}
                    onClick={() => setInputs((current) => ({ ...current, ...options.defaults }))}
                    sx={{ textTransform: "none", px: 0 }}
                  >
                    Reset to my bot's settings
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        {/* ---- Result ---- */}
        <Grid item xs={12} md={5}>
          <Stack spacing={3} sx={{ position: { md: "sticky" }, top: { md: 16 } }}>
            <Paper elevation={0} sx={paperStyle} data-testid="calc-result">
              <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <SectionTitle>Estimated monthly cost</SectionTitle>
                  {loading && <CircularProgress size={18} />}
                </Stack>
                {error && <Alert severity="error">{error}</Alert>}
                {result && (
                  <>
                    <Typography sx={{ ...typography.h1, color: ColorsConstants.primaryBlue100 }} data-testid="calc-total">
                      {money(result.monthly_cost)}
                    </Typography>
                    <Typography sx={{ ...typography.bodyLarge, color: "text.secondary" }} data-testid="calc-total-detail">
                      {formatNumber(result.questions_per_month)} questions
                      · {money(result.cost_per_question)} per question
                    </Typography>
                    <Typography sx={{ ...typography.bodySmall, color: "text.secondary" }}>
                      {result.basis_used === "measured"
                        ? `Based on your last ${result.measured.days} days (${formatNumber(result.measured.questions)} questions).`
                        : "Estimated from your settings."}
                      {rateText && ` ${rateText}.`}
                      {result.currency_fallback &&
                        ` Shown in USD: no conversion rate is set up for your billing currency (${result.currency_fallback}).`}
                    </Typography>
                    <Divider />
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Per question</TableCell>
                          <TableCell align="right">In</TableCell>
                          <TableCell align="right">Out</TableCell>
                          <TableCell align="right">Monthly</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.breakdown
                          .filter((line) => line.input_tokens || line.output_tokens)
                          .map((line) => (
                            <TableRow key={line.call}>
                              <TableCell>{CALL_LABELS[line.call] || line.call}</TableCell>
                              <TableCell align="right">{formatNumber(line.input_tokens)}</TableCell>
                              <TableCell align="right">{formatNumber(line.output_tokens)}</TableCell>
                              <TableCell align="right">{money(line.monthly_cost)}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                    <Typography sx={{ ...typography.bodySmall, color: "text.secondary" }} data-testid="calc-training">
                      One-off: training your current knowledge base ({formatNumber(result.training.chunks)} chunks,{" "}
                      {formatNumber(result.training.tokens)} tokens) costs about{" "}
                      {money(result.training_cost)}.
                    </Typography>
                  </>
                )}
              </Stack>
            </Paper>
          </Stack>
        </Grid>

        {/* ---- Comparison ---- */}
        {result && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={paperStyle}>
              <SectionTitle>Same usage on other chat models</SectionTitle>
              <Table size="small" sx={{ mt: 1 }} data-testid="calc-comparison">
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Price per 1M tokens (in / out)</TableCell>
                    <TableCell align="right">Per question</TableCell>
                    <TableCell align="right">Monthly</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.comparison.map((row) => (
                    <TableRow
                      key={row.model}
                      hover
                      selected={row.selected}
                      onClick={() => setModel(row.model)}
                      sx={{ cursor: "pointer", "&.Mui-selected": { bgcolor: "#E8EEF9" } }}
                    >
                      <TableCell sx={{ fontWeight: row.selected ? 600 : 400 }}>{row.display_name}</TableCell>
                      <TableCell align="right">
                        ${row.input_per_mtok} / ${row.output_per_mtok}
                      </TableCell>
                      <TableCell align="right">{money(row.cost_per_question)}</TableCell>
                      <TableCell align="right">{money(row.monthly_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}

        {result && result.embedding_comparison?.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={paperStyle}>
              <SectionTitle>Embedding models</SectionTitle>
              <Typography sx={{ ...typography.bodySmall, color: "text.secondary", mt: 0.5 }}>
                Lookups happen on every question; training is a one-off for your current knowledge base (
                {formatNumber(result.training.chunks)} chunks). Switching embedding model means re-training.
              </Typography>
              <Table size="small" sx={{ mt: 1 }} data-testid="calc-embedding-comparison">
                <TableHead>
                  <TableRow>
                    <TableCell>Model</TableCell>
                    <TableCell align="right">Price per 1M tokens</TableCell>
                    <TableCell align="right">Lookups per month</TableCell>
                    <TableCell align="right">Training (one-off)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.embedding_comparison.map((row) => (
                    <TableRow
                      key={row.model}
                      hover
                      selected={row.selected}
                      onClick={() => setEmbeddingModel(row.model)}
                      sx={{ cursor: "pointer", "&.Mui-selected": { bgcolor: "#E8EEF9" } }}
                    >
                      <TableCell sx={{ fontWeight: row.selected ? 600 : 400 }}>{row.display_name}</TableCell>
                      <TableCell align="right">${row.input_per_mtok}</TableCell>
                      <TableCell align="right">{money(row.monthly_lookup_cost)}</TableCell>
                      <TableCell align="right">{money(row.training_cost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
        )}

        {result && (
          <Grid item xs={12}>
            <CostExplainer
              priceSource={result.explanation.price_source}
              pricesLastUpdated={result.explanation.prices_last_updated}
              prices={[
                { label: result.prices.chat.display_name, ...result.prices.chat },
                { label: `${result.prices.embedding.display_name} (lookups and training)`, ...result.prices.embedding },
              ]}
              formula={`${result.explanation.formula}. Monthly cost = cost per question × customers × questions per customer.`}
              steps={tokenStepLines(result.explanation, result.basis_used)}
              wordsNote={result.explanation.words_note}
              exchangeRate={rateText}
              exclusions={result.explanation.exclusions}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AITokenCalculator;
